import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControlLabel,
  Checkbox,
  Alert,
  CircularProgress
} from '@mui/material';
import { saveFilter } from '../../services/savedFiltersService';

/**
 * Dialog for saving a filter configuration with overrides
 * @param {boolean} open - Whether the dialog is open
 * @param {function} onClose - Function to call when dialog is closed
 * @param {Object} filterConfig - Complete filter configuration including columnConfig
 * @param {string} filterId - ID of the filter to save (optional)
 * @param {Object} overrides - User overrides to save with the filter (optional)
 */
const SaveFilterDialog = ({ open, onClose, filterConfig, filterId, overrides }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isFavorite, setIsFavorite] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  // Log props when component receives them
  useEffect(() => {
    console.log('SaveFilterDialog received filterConfig:', filterConfig);
    console.log('SaveFilterDialog received overrides:', overrides);
  }, [filterConfig, overrides]);

  const handleSubmit = async () => {
    if (!name.trim()) {
      setError('Template name is required');
      return;
    }
    
    try {
      setError('');
      setLoading(true);
      
      // Ensure the filter configuration has the standardized columnConfig structure
      let standardizedFilterConfig = {};
      
      if (filterConfig) {
        standardizedFilterConfig = {
          ...filterConfig,
          // Ensure columnConfig exists
          columnConfig: filterConfig.columnConfig || {}
        };
        
        // If selectedColumns exists but there's no corresponding entry in columnConfig,
        // create empty objects for them
        if (standardizedFilterConfig.selectedColumns && Array.isArray(standardizedFilterConfig.selectedColumns)) {
          standardizedFilterConfig.selectedColumns.forEach(colName => {
            if (!standardizedFilterConfig.columnConfig[colName]) {
              standardizedFilterConfig.columnConfig[colName] = { instructions: '' };
            }
          });
        }
        
        console.log('Standardized filter config:', standardizedFilterConfig);
      }
      
      console.log('SaveFilterDialog submitting with overrides:', overrides);
      // Get the original filter ID from localStorage, if we're modifying an existing filter
      const originalFilterId = localStorage.getItem('savedFilterId');
      console.log('Original filter ID for override fetching:', originalFilterId);
      
      const saveData = {
        name,
        description,
        filterId,
        originalFilterId, // Include the original filter ID so we can fetch its overrides
        filterConfig: standardizedFilterConfig,
        overrides,
        isFavorite
      };
      // DIAGNOSTIC: Check specifically for column instructions before saving
      console.log('=================== DIAGNOSTIC CHECKPOINT ===================');
      console.log('FINAL CHECK BEFORE SAVING FILTER:');
      if (standardizedFilterConfig && standardizedFilterConfig.columnConfig) {
        Object.keys(standardizedFilterConfig.columnConfig).forEach(column => {
          const instructions = standardizedFilterConfig.columnConfig[column]?.instructions || '';
          console.log(`  - ${column}: "${instructions}"`);
        });
      } else {
        console.log('  No columnConfig found in standardizedFilterConfig!');
      }
      console.log('============================================================');
      console.log('Full save data being sent:', saveData);
      
      const response = await saveFilter(saveData);
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to save filter');
      }
      
      setLoading(false);
      onClose(true); // Close with success flag
    } catch (error) {
      console.error('Error saving filter:', error);
      setError(error.message || 'An error occurred while saving the filter');
      setLoading(false);
    }
  };
  
  const handleClose = () => {
    // Reset form when closing
    setName('');
    setDescription('');
    setIsFavorite(false);
    setError('');
    onClose(false);
  };
  
  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Save Template</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        <TextField
          autoFocus
          margin="dense"
          label="Template Name"
          fullWidth
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          sx={{ mb: 2 }}
        />
        
        <TextField
          margin="dense"
          label="Description (optional)"
          fullWidth
          multiline
          rows={3}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          sx={{ mb: 2 }}
        />
        
        <FormControlLabel
          control={
            <Checkbox
              checked={isFavorite}
              onChange={(e) => setIsFavorite(e.target.checked)}
            />
          }
          label="Save as favorite"
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          disabled={loading || !name.trim()}
        >
          {loading ? <CircularProgress size={24} /> : 'Save Template'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SaveFilterDialog;
